import classNames from 'classnames'
import formatDistance from 'date-fns/formatDistance'
import parseISO from 'date-fns/parseISO'
import * as React from 'react'

import {useIsAutodetectTimeZone} from '../../../hooks/useIsAutodetectTimeZone'
import useTimeSelector from '../../../hooks/useTimeSelector'
import {displayDate} from '../../../utils/dateTimeUtils'
import BuildPath from '../BuildPath/BuildPath'
import Link from '../Link/Link'
import RouterLinkWrapper from '../RouterLinkWrapper/RouterLinkWrapper'

import type {Props} from './BuildTriggeredBy.types'

import styles from './BuildTriggeredBy.css'

const delimiter = ';'
const snapshotText = 'Snapshot dependency'
const buildNumberText = ', build '
const buildNumberSymbol = '#'

function BuildTriggeredBy({
  text,
  buildNumber,
  buildUrl,
  buildPath,
  date,
  skipDate,
  withFullDate,
  className,
  withoutLabel,
  inline,
  inTable,
}: Props) {
  const isAutodetectTimeZone = useIsAutodetectTimeZone()

  function getParsedContent(): React.ReactNode {
    if (text == null) {
      return null
    }

    let unparsedText = text
    let preSnapshotText
    let snapshotLinkEl
    let preBuildPathText
    let buildPathEl
    let preBuildNumberText
    let buildNumberEl

    if (buildUrl != null) {
      const snapshotIndex = unparsedText.indexOf(`${snapshotText}${delimiter}`)

      if (
        snapshotIndex !== -1 &&
        (snapshotIndex === 0 ||
          unparsedText[snapshotIndex - 2] === delimiter ||
          unparsedText[snapshotIndex - 1] === ' ')
      ) {
        if (snapshotIndex > 0) {
          preSnapshotText = unparsedText.substring(0, snapshotIndex)
        }

        snapshotLinkEl = (
          <RouterLinkWrapper>
            <Link href={buildUrl}>{snapshotText}</Link>
          </RouterLinkWrapper>
        )
        unparsedText = unparsedText.substring(snapshotIndex + snapshotText.length)
      }
    }

    if (buildPath?.length) {
      const textBuildPath = buildPath.map(path => path.name).join(' / ')
      const pathIndex = unparsedText.indexOf(textBuildPath)

      if (
        pathIndex !== -1 &&
        (pathIndex === 0 ||
          (unparsedText[pathIndex - 2] === delimiter && unparsedText[pathIndex - 1] === ' '))
      ) {
        if (pathIndex > 0) {
          preBuildPathText = unparsedText.substring(0, pathIndex)
        }

        buildPathEl = (
          <BuildPath
            path={buildPath}
            className={styles.buildPath}
            linkClassName={styles.buildPathLink}
          />
        )
        unparsedText = unparsedText.substring(pathIndex + textBuildPath.length)

        if (
          buildNumber != null &&
          buildUrl != null &&
          unparsedText.indexOf(`${buildNumberText}${buildNumberSymbol}${buildNumber}`) === 0
        ) {
          preBuildNumberText = buildNumberText
          buildNumberEl = (
            <RouterLinkWrapper>
              <Link href={buildUrl}>{`${buildNumberSymbol}${buildNumber}`}</Link>
            </RouterLinkWrapper>
          )
          unparsedText = unparsedText.substring(
            `${buildNumberText}${buildNumberSymbol}${buildNumber}`.length,
          )
        }
      }
    }

    return (
      <React.Fragment>
        {preSnapshotText}
        {snapshotLinkEl}
        {preBuildPathText}
        {buildPathEl}
        {preBuildNumberText}
        {buildNumberEl}
        {unparsedText}
      </React.Fragment>
    )
  }

  const dateString = useTimeSelector(now => {
    if (date == null || skipDate === true) {
      return ''
    }

    return ` ${
      withFullDate === true
        ? displayDate(date, isAutodetectTimeZone, true)
        : formatDistance(parseISO(date), now, {
            addSuffix: true,
          })
    }`
  })

  if (text == null) {
    return null
  }

  const classes = classNames(className, {
    [styles.inline]: inline,
  })
  return inTable ? (
    <tr className={classes} data-test-build-triggered-by>
      <th>{'Triggered'}</th>
      <td>
        {dateString && `${dateString}: `}
        {getParsedContent()}
      </td>
    </tr>
  ) : (
    <div className={classes} data-test-build-triggered-by>
      {withoutLabel !== true && `Triggered${dateString}: `}
      {getParsedContent()}
    </div>
  )
}

BuildTriggeredBy.defaultProps = {
  inline: true,
}

export default BuildTriggeredBy
